import type { ReactElement, ReactNode } from "react";

/**
 * The log's line and block components (ADR-068 decision 8 and its amendment of 2026-10-27): `ChatLine` is the
 * paragraph the page's own system, death and error lines are drawn in, behind their marks, and `ChatBlock` a reply
 * of several rows drawn as one message, its header on a row of its own and its rows in aligned columns, which
 * `/ping`'s panel uses. A line a player sent, and a scope's lines, are drawn in `log.tsx`. Both take what to say as
 * data, so a command whose reply has rows can use the same block.
 */

/**
 * A line of the log: one paragraph whose class and `data-chat-line` name its kind.
 *
 * @param props The line's `kind` (`system`, `death` or `error`), the `mark` drawn before it and its
 *   `children`.
 * @returns The paragraph.
 */
export function ChatLine({ kind, mark, children }: { readonly kind: string; readonly mark?: ReactNode; readonly children: ReactNode }): ReactElement {
  return (
    <p className={`ot-chat__line ot-chat__line--${kind}`} data-chat-line={kind}>
      {mark}
      {children}
    </p>
  );
}

/**
 * One row of a block: its key, a label, a value with an optional range beside it and a meaning for the tooltip.
 * A row that is not `available` has its value span the value and range columns.
 */
export interface ChatBlockRow {
  readonly key: string;
  readonly label: string;
  readonly value: string;
  readonly range: string;
  readonly meaning: string;
  readonly available: boolean;
}

/**
 * A reply of several rows. The visually hidden `spoken` sentence is what a screen reader hears, as one message; the
 * grid is hidden from it, since its cells would read with no pause between them.
 *
 * @param props The block's `kind` (its `data-chat-line`), the `tone` of the line colour it is drawn in, `hook`
 *   (the name its parts carry in `data-<hook>` and `data-<hook>-row` attributes, which its caller's tests read),
 *   `spoken` message, `mark` and `header` text, its `rows`, and `mono`, which sets it in the monospaced face.
 * @returns The block.
 */
export function ChatBlock({ kind, tone, hook, spoken, mark, header, rows, mono = false }: { readonly kind: string; readonly tone: string; readonly hook: string; readonly spoken: string; readonly mark: ReactNode; readonly header: string; readonly rows: readonly ChatBlockRow[]; readonly mono?: boolean }): ReactElement {
  const part = (name: string, value?: string): Record<string, string> => (name === "row" ? { [`data-${hook}-row`]: value ?? "" } : { [`data-${hook}`]: name });
  return (
    <div className={`ot-chat__line ot-chat__line--${tone} ot-chat__block${mono ? " ot-chat__block--mono" : ""}`} data-chat-line={kind}>
      <span className="ot-chat__spoken" data-chat="spoken">
        {spoken}
      </span>
      <div aria-hidden="true">
        <div className="ot-chat__block-header" {...part("header")}>
          {mark}
          {header}
        </div>
        <div className="ot-chat__block-rows">
          {rows.map((row) => (
            <div key={row.key} className="ot-chat__block-row" {...part("row", row.key)} data-chat-full={row.meaning}>
              <span className="ot-chat__block-label" {...part("label")}>
                {row.label}
              </span>
              {row.available ? (
                <>
                  <span className="ot-chat__block-value" {...part("value")}>
                    {row.value}
                  </span>
                  <span {...part("range")}>{row.range}</span>
                </>
              ) : (
                <span className="ot-chat__block-value ot-chat__block-value--none" {...part("value")}>
                  {row.value}
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
